/**
 * Applications Store (Pinia)
 * Manages applications list, application details, and related products
 * Implements fallback strategy: API first, then Mock data
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import * as applicationsApi from '@/api/applications'
import { applications as mockApplications } from '@/data/applications'

export const useApplicationsStore = defineStore('applications', () => {
  // State
  const applications = ref([])
  const currentApplication = ref(null)
  const applicationProducts = ref([])
  const bundleProducts = ref([])
  const loading = ref(false)
  const error = ref(null)
  const usingMockData = ref(false)

  // Computed properties
  const hasError = computed(() => !!error.value)

  /**
   * Fetch applications list (Product Fields)
   * Implements fallback strategy: try API first, then fallback to Mock
   * @returns {Promise<Object>} Response data
   */
  async function fetchApplications() {
    loading.value = true
    error.value = null

    try {
      // Try API first
      const result = await applicationsApi.getApplications()

      if (result.code === 1 && result.data) {
        // API success - result.data is directly an array
        const appsList = Array.isArray(result.data) ? result.data : result.data.list || []
        // Sort by sort field (ascending) to maintain order
        applications.value = appsList.sort((a, b) => (a.sort || 0) - (b.sort || 0))
        usingMockData.value = false
        return { success: true, data: applications.value }
      } else {
        // API returned error code, fallback to Mock
        useMockData()
        return { success: false, data: null }
      }
    } catch (err) {
      // Network error, fallback to Mock
      console.error('Error fetching applications from API:', err)
      useMockData()
      return { success: false, data: null }
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch application detail by ID
   * Implements fallback strategy: try API first, then fallback to Mock
   * @param {number|string} id - Application ID
   * @returns {Promise<Object|null>} Application object or null if not found
   */
  async function fetchApplicationById(id) {
    loading.value = true
    error.value = null

    try {
      // Try API first
      const result = await applicationsApi.getApplicationById(id)

      if (result.code === 1 && result.data) {
        // API success
        currentApplication.value = result.data
        console.log(currentApplication, '------------')
        usingMockData.value = false
        return result.data
      } else {
        // API returned error code, fallback to Mock
        const mockApp = mockApplications.find(a => a.id === id)
        if (mockApp) {
          currentApplication.value = mockApp
          usingMockData.value = true
          return mockApp
        } else {
          error.value = '应用领域不存在'
          currentApplication.value = null
          return null
        }
      }
    } catch (err) {
      // Network error, fallback to Mock
      console.error('Error fetching application from API:', err)

      const mockApp = mockApplications.find(a => a.id === id)
      if (mockApp) {
        currentApplication.value = mockApp
        usingMockData.value = true
        return mockApp
      } else {
        error.value = '应用领域不存在'
        currentApplication.value = null
        return null
      }
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch products for a specific application field
   * @param {number} applicationFieldId - Application field ID
   * @param {Object} params - Query parameters (page, pageSize, etc.)
   * @returns {Promise<Object>} Response data with products
   */
  async function fetchProductsByApplicationField(applicationFieldId, params = {}) {
    loading.value = true
    error.value = null

    try {
      // Try API first
      const result = await applicationsApi.getProductsByApplicationField(
        applicationFieldId,
        params
      )

      // Handle different response formats
      let productList = []

      // Check if result is an array (direct product list)
      if (Array.isArray(result)) {
        productList = result
      }
      // Check if result follows standard API response format
      else if (result.code === 1 && result.data) {
        productList = result.data.list || result.data || []
      }

      if (productList.length > 0) {
        // API success - 直接使用 API 返回的数据，不进行适配转换
        applicationProducts.value = productList
        usingMockData.value = false
        return { success: true, data: productList }
      } else {
        // No products found
        error.value = result.message || '暂无产品'
        applicationProducts.value = []
        return { success: false, data: null }
      }
    } catch (err) {
      // Network error
      console.error('Error fetching products by application field:', err)
      error.value = err.message || '获取产品列表失败'
      applicationProducts.value = []
      return { success: false, data: null }
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch bundle products
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Response data
   */
  async function fetchBundleProducts(params = {}) {
    loading.value = true
    error.value = null

    try {
      const result = await applicationsApi.getBundleProducts(params)
      let bundleList = []
      if (Array.isArray(result)) {
        bundleList = result
      } else if (result.code === 1 && result.data) {
        bundleList = result.data.list || result.data || []
      }

      bundleProducts.value = bundleList
      return { success: true, data: bundleList }
    } catch (err) {
      console.error('Error fetching bundle products:', err)
      error.value = err.message || '获取套餐产品失败'
      bundleProducts.value = []
      return { success: false, data: null }
    } finally {
      loading.value = false
    }
  }

  /**
   * Use Mock data (fallback strategy)
   * Loads all mock applications
   */
  function useMockData() {
    applications.value = mockApplications
    usingMockData.value = true
  }

  /**
   * Set applications list (direct set)
   * @param {Array} newApplications - New applications array
   */
  function setApplications(newApplications) {
    applications.value = newApplications
  }

  /**
   * Set current application (direct set)
   * @param {Object} application - Application object
   */
  function setCurrentApplication(application) {
    currentApplication.value = application
  }

  /**
   * Set application products (direct set)
   * @param {Array} products - Products array
   */
  function setApplicationProducts(products) {
    applicationProducts.value = products
  }

  /**
   * Clear current application
   */
  function clearCurrentApplication() {
    currentApplication.value = null
  }

  /**
   * Clear applications list
   */
  function clearApplications() {
    applications.value = []
  }

  /**
   * Clear application products
   */
  function clearApplicationProducts() {
    applicationProducts.value = []
  }

  /**
   * Clear error
   */
  function clearError() {
    error.value = null
  }

  return {
    // State
    applications,
    currentApplication,
    applicationProducts,
    bundleProducts,
    loading,
    error,
    usingMockData,

    // Computed
    hasError,

    // Actions
    fetchApplications,
    fetchApplicationById,
    fetchProductsByApplicationField,
    fetchBundleProducts,
    useMockData,
    setApplications,
    setCurrentApplication,
    setApplicationProducts,
    clearCurrentApplication,
    clearApplications,
    clearApplicationProducts,
    clearError
  }
})
